// ============================================
// BLOKAS 1: [useApiKeys.ts] Modulio importai ir inicializacija
// ============================================

import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { ApiKey } from '../utils/types';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

export const useApiKeys = () => {
  // 📦 SUB-BLOKAS 1.1: State kintamieji
  console.log('📦 [SUB-BLOKAS 1.1 useApiKeys.ts] State kintamieji inicijuojami.');
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  // ============================================
  // BLOKAS 2: [useApiKeys.ts] Pagrindinė API raktų gavimo funkcija
  // ============================================
  const fetchApiKeys = async () => {
    console.log('🚀 [BLOKAS 2.1 useApiKeys.ts] fetchApiKeys funkcija iškviesta.');
    if (!user?.id) {
      console.log('ℹ️  [SUB-BLOKAS 2.2 useApiKeys.ts] Vartotojas neprisijungęs, grąžinamas tuščias sąrašas.');
      setApiKeys([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    console.log('🔄 [SUB-BLOKAS 2.3 useApiKeys.ts] Pradedamas API raktų gavimas iš duomenų bazės.');

    try {
      const { data, error: fetchError } = await supabase
        .from('api_keys')
        .select('*')
        .eq('user_id', user.id);

      if (fetchError) {
        console.error('❌ [SUB-BLOKAS 2.4 useApiKeys.ts] Klaida gaunant API raktus:', fetchError);
        throw fetchError;
      }

      console.log(`✅ [SUB-BLOKAS 2.5 useApiKeys.ts] Gauta ${data?.length || 0} API raktų.`);
      setApiKeys(data || []);
    } catch (err: any) {
      const errorMessage = err.message || 'Nepavyko gauti API raktų';
      console.error('❌ [SUB-BLOKAS 2.6 useApiKeys.ts] Kritinė klaida:', errorMessage);
      setError(errorMessage);
      toast.error('Nepavyko įkelti API raktų');
    } finally {
      console.log('🏁 [SUB-BLOKAS 2.7 useApiKeys.ts] fetchApiKeys funkcija baigta.');
      setLoading(false);
    }
  };

  // ============================================
  // BLOKAS 3: [useApiKeys.ts] API rakto pridėjimo funkcija (SU TIKSLIA PARAMETRŲ TVARKA)
  // ============================================
  const addApiKey = async (
    botId: string,
    exchangeName: string,
    apiKeyPublic: string,
    apiSecret: string,
    accountName: string = 'Pagrindinė paskyra'
  ) => {
    console.log('🚀 [BLOKAS 3.1 useApiKeys.ts] addApiKey funkcija iškviesta.');
    
    // 📋 SUB-BLOKAS 3.2: Išsamus parametrų tikrinimas
    console.log('📋 [SUB-BLOKAS 3.2 useApiKeys.ts] Detalus parametrų tikrinimas:');
    console.log('  • botId:', botId, '(tipas:', typeof botId, ')');
    console.log('  • exchangeName:', exchangeName);
    console.log('  • apiKeyPublic pradžia:', apiKeyPublic.substring(0, 8), '...');
    console.log('  • apiSecret pradžia:', apiSecret.substring(0, 3), '...');
    console.log('  • accountName:', accountName || 'Pagrindinė paskyra');
    console.log('  • userId:', user?.id);

    if (!user?.id) {
      console.error('❌ [SUB-BLOKAS 3.2] Klaida: Vartotojas neprisijungęs.');
      toast.error('Norint pridėti API raktus, reikia būti prisijungus.');
      return Promise.resolve(false);
    }

    // Patikriname, ar botId yra validus UUID
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(botId)) {
      console.error('❌ [SUB-BLOKAS 3.2] Klaida: Netinkamas botId formatas:', botId);
      toast.error('Netinkamas bot ID formatas. Pabandykite iš naujo.');
      return Promise.resolve(false);
    }

    if (!botId || !exchangeName || !apiKeyPublic.trim() || !apiSecret.trim()) {
      console.error('❌ [SUB-BLOKAS 3.2] Klaida: Trūksta privalomų laukų.');
      toast.error('Visi laukai yra privalomi.');
      return Promise.resolve(false);
    }

    try {
      console.log('🔄 [SUB-BLOKAS 3.3 useApiKeys.ts] Kvietiama duomenų bazės funkcija f_upsert_api_credentials.');
      
      // SVARBU: Parametrų TVARKA TURI BŪTI TOKIA:
      // 1. p_account_name (TEXT)
      // 2. p_api_key (TEXT)
      // 3. p_api_secret (TEXT)
      // 4. p_bot_id (UUID)
      // 5. p_exchange (TEXT)
      // 6. p_user_id (UUID)
      
      const requestParams = {
  p_account_name: accountName || 'Pagrindinė paskyra',
  p_api_key: apiKeyPublic.trim(),
  p_api_secret: apiSecret.trim(),       // Dabar funkcija priims šį parametrą
  p_bot_id: botId,
  p_exchange_name: exchangeName,        // SVARBU: p_exchange_name, ne p_exchange
  p_user_id: user.id
};

      console.log('📤 [SUB-BLOKAS 3.3] Siunčiami parametrai:', {
        ...requestParams,
        p_api_key: `${requestParams.p_api_key.substring(0, 8)}...`,
        p_api_secret: `${requestParams.p_api_secret.substring(0, 3)}...`
      });

      const { data, error: rpcError } = await supabase.rpc(
        'f_upsert_api_credentials',
        requestParams
      );

      if (rpcError) {
        console.error('❌ [SUB-BLOKAS 3.4 useApiKeys.ts] Klaida kviečiant RPC:', {
          code: rpcError.code,
          message: rpcError.message,
          details: rpcError.details,
          hint: rpcError.hint
        });
        
        // Papildoma informacija klaidai
        if (rpcError.code === 'PGRST202') {
          console.error('❌ [SUB-BLOKAS 3.4] Schema cache problema. Perjunkite puslapį ir bandykite vėl.');
          toast.error('Serverio klaida. Perjunkite puslapį ir bandykite vėl.');
        } else {
          throw rpcError;
        }
        return Promise.resolve(false);
      }

      console.log('✅ [SUB-BLOKAS 3.5 useApiKeys.ts] RPC funkcija sėkmingai įvykdyta:', data);
      toast.success(`API raktas platformai "${exchangeName}" sėkmingai išsaugotas.`);

      await fetchApiKeys(); // Atnaujiname sąrašą
      return Promise.resolve(true);
    } catch (err: any) {
      const errorMsg = err.message || 'Nepavyko išsaugoti API rakto.';
      console.error('❌ [SUB-BLOKAS 3.6 useApiKeys.ts] Kritinė klaida:', {
        message: err.message,
        code: err.code,
        stack: err.stack
      });
      
      // Konkrečios klaidos pranešimas
      let userMessage = errorMsg;
      if (err.message.includes('Not authenticated')) {
        userMessage = 'Prisijungimo klaida. Prisijunkite iš naujo.';
      } else if (err.message.includes('duplicate key')) {
        userMessage = 'API raktas jau egzistuoja šiam bot\'ui ir platformai.';
      }
      
      toast.error(userMessage);
      return Promise.resolve(false);
    }
  };

  // ============================================
  // BLOKAS 4: [useApiKeys.ts] API rakto ištrynimo funkcija
  // ============================================
  const deleteApiKey = async (apiKeyId: string) => {
    console.log(`🗑️  [BLOKAS 4.1 useApiKeys.ts] deleteApiKey funkcija iškviesta, rakto ID: ${apiKeyId}`);
    if (!user?.id) {
      toast.error('Norint ištrinti API raktus, reikia būti prisijungus.');
      return false;
    }

    try {
      const { error: deleteError } = await supabase
        .from('api_keys')
        .delete()
        .eq('id', apiKeyId)
        .eq('user_id', user.id);

      if (deleteError) {
        throw deleteError;
      }

      console.log('✅ [SUB-BLOKAS 4.2 useApiKeys.ts] API raktas sėkmingai ištrintas.');
      toast.success('API raktas sėkmingai ištrintas.');
      setApiKeys(apiKeys.filter(key => key.id !== apiKeyId));
      return true;
    } catch (err: any) {
      console.error('❌ [SUB-BLOKAS 4.3 useApiKeys.ts] Klaida trinant:', err);
      toast.error(err.message || 'Nepavyko ištrinti API rakto.');
      return false;
    }
  };

  // ============================================
  // BLOKAS 5: [useApiKeys.ts] Efektas, paleidžiantis raktų gavimą
  // ============================================
  useEffect(() => {
    console.log('🔗 [BLOKAS 5.1 useApiKeys.ts] useEffect paleistas.');
    fetchApiKeys();
  }, [user?.id]);

  // ============================================
  // BLOKAS 6: [useApiKeys.ts] Rezultatų grąžinimas su VALDYMO FUNKCIJOMIS
  // ============================================
  console.log('📤 [BLOKAS 6.1 useApiKeys.ts] Grąžinamos funkcijos ir state.');
  
  // 📊 SUB-BLOKAS 6.2: Papildomos valdymo funkcijos
  const updateApiKeyStatus = async (apiKeyId: string, status: string) => {
    if (!user?.id) return false;
    
    try {
      const { error } = await supabase
        .from('api_keys')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', apiKeyId)
        .eq('user_id', user.id);
      
      if (error) throw error;
      
      // Atnaujiname lokalų state
      setApiKeys(apiKeys.map(key => 
        key.id === apiKeyId ? { ...key, status, updated_at: new Date().toISOString() } : key
      ));
      
      return true;
    } catch (err) {
      console.error('Klaida atnaujinant statusą:', err);
      return false;
    }
  };

  // 📊 SUB-BLOKAS 6.3: API rakto patikrinimo funkcija
  const testApiKey = async (apiKeyId: string) => {
    const key = apiKeys.find(k => k.id === apiKeyId);
    if (!key) return false;
    
    // Čia galima pridėti tikrą API rakto testavimo logiką
    console.log(`🧪 Testuojamas API raktas: ${key.exchange} (${key.id})`);
    
    // Laikinai grąžiname sėkmę
    await updateApiKeyStatus(apiKeyId, 'connected');
    return true;
  };

  return {
    // State
    apiKeys,
    loading,
    error,
    
    // Pagrindinės funkcijos
    fetchApiKeys,
    addApiKey,
    deleteApiKey,
    
    // Papildomos valdymo funkcijos (naujos)
    updateApiKeyStatus,
    testApiKey,
    
    // Statistikos funkcijos
    getConnectedKeys: () => apiKeys.filter(k => k.status === 'connected').length,
    getTotalKeys: () => apiKeys.length
  };
};

export default useApiKeys;